import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { auth } from "@/firebase";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { sendVerificationEmail } from "@/lib/sendVerificationEmail";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [cooldownLeftMs, setCooldownLeftMs] = useState(0);

  const email = user?.email || auth.currentUser?.email || "";
  const emailVerified = !!(user?.emailVerified ?? auth.currentUser?.emailVerified);
  const cooldownMs = 60_000; // Prevent spamming resend requests

  const getLastResendAt = () => {
    if (!email) return null;
    try {
      const raw = localStorage.getItem(
        `emailVerificationResendAt:${email.toLowerCase()}`
      );
      const ts = raw ? Number(raw) : NaN;
      return Number.isFinite(ts) ? ts : null;
    } catch {
      return null;
    }
  };

  const getCooldownLeft = () => {
    const last = getLastResendAt();
    if (!last) return 0;
    const left = cooldownMs - (Date.now() - last);
    return left > 0 ? left : 0;
  };

  const setLastResendAt = () => {
    if (!email) return;
    try {
      localStorage.setItem(
        `emailVerificationResendAt:${email.toLowerCase()}`,
        String(Date.now())
      );
    } catch {
      // ignore
    }
  };

  const updateCooldown = () => {
    const last = getLastResendAt();
    if (!last) {
      setCooldownLeftMs(0);
      return;
    }
    const left = cooldownMs - (Date.now() - last);
    setCooldownLeftMs(left > 0 ? left : 0);
  };

  useEffect(() => {
    updateCooldown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (emailVerified) {
      navigate("/", { replace: true });
    }
  }, [emailVerified, navigate]);

  const refreshStatus = async () => {
    setLocalError(null);
    const current = auth.currentUser;
    if (!current) {
      navigate("/login", { replace: true });
      return;
    }
    setIsSubmitting(true);
    try {
      await current.reload();
      if (current.emailVerified) {
        navigate("/", { replace: true });
      }
    } catch (e) {
      setLocalError("حدث خطأ أثناء التحقق. جرّب مرة أخرى.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendVerification = async () => {
    setLocalError(null);
    const current = auth.currentUser;
    if (!current) {
      navigate("/login", { replace: true });
      return;
    }
    if (!current.email) {
      setLocalError("لا يوجد بريد إلكتروني للتحقق منه.");
      return;
    }
    const left = getCooldownLeft();
    if (left > 0) {
      setLocalError(
        `انتظر ${Math.ceil(left / 1000)} ثانية قبل إعادة إرسال رسالة التحقق.`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      setLastResendAt();
      await sendVerificationEmail({
        email: current.email || undefined,
        displayName: current.displayName || undefined,
      });
    } catch {
      setLocalError("تعذر إرسال رسالة التحقق الآن. جرّب لاحقاً.");
    } finally {
      setIsSubmitting(false);
      updateCooldown();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" dir="rtl">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-primary/10 to-transparent" />
        <div className="relative pt-12 pb-6 px-6 text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-primary/15 text-primary mx-auto flex items-center justify-center">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            تأكيد البريد الإلكتروني
          </h1>
          <p className="text-sm text-muted-foreground">
            خطوة أخيرة لحماية حسابك قبل الدخول للتطبيق.
          </p>
        </div>
      </div>

      <div className="flex-1 px-6 pb-8 flex items-start">
        <Card className="max-w-md mx-auto border-2 w-full overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 text-primary">
              <Mail className="h-5 w-5" />
              <h2 className="text-lg font-semibold text-foreground">
                تحقق من صندوق بريدك
              </h2>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {localError && (
              <Alert variant="destructive">
                <AlertDescription>{localError}</AlertDescription>
              </Alert>
            )}

            {email ? (
              <p className="text-sm text-muted-foreground">
                أرسلنا رسالة تحقق إلى:
                <span className="text-foreground font-medium"> {email}</span>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                لا يظهر لدينا بريد إلكتروني لهذا الحساب.
              </p>
            )}

            <p className="text-sm text-muted-foreground">
              بعد تأكيد البريد من داخل الإيميل، اضغط زر “تم التحقق” للتحقق مرة أخرى.
            </p>

            <Button
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={refreshStatus}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin ml-2" />
              ) : (
                "تم التحقق"
              )}
            </Button>

            <Button
              className="w-full h-12 rounded-full"
              variant="secondary"
              onClick={resendVerification}
              disabled={isSubmitting || !email || cooldownLeftMs > 0}
            >
              إعادة إرسال رسالة التحقق
            </Button>

            <p className="text-xs text-muted-foreground">
              قد تصل الرسالة إلى مجلد “الرسائل غير المرغوب فيها”.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

